import React from 'react';

export function useWebComponentWrapper(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: any,
  componentRef: React.RefObject<HTMLElement>,
): void {
  // Pick out the props that are functions
  const functionKeys = React.useMemo(() => {
    return Object.keys(props).filter((key) => typeof props[key] === 'function');
  }, [props]);

  // Data props to be passed to the Web Component, which have the
  // functions filtered out
  const dataProps = React.useMemo(() => {
    const p = { ...props };
    functionKeys.forEach((key) => delete p[key]);
    delete p.children;
    const pKeys = Object.keys(p);
    pKeys.forEach((key) => {
      // Stringify non-natively supported prop types (non String/Boolean)
      if (typeof p[key] === 'string' || typeof p[key] === 'boolean') return;
      p[key] = JSON.stringify(p[key]);
    });
    return p;
  }, [props, functionKeys]);

  // Bind dataProps to Web Component as attributes
  React.useEffect(() => {
    const component = componentRef.current;
    if (!component) return;
    const dataKeys = Object.keys(dataProps);
    let changeDetected = false;
    dataKeys.forEach((key) => {
      if (component.getAttribute(key) !== dataProps[key]) {
        changeDetected = true;
        component.setAttribute(key, dataProps[key]);
      }
    });
    // @ts-ignore Force Web Component Re-render
    changeDetected && component.forceUpdate();
  }, [componentRef, dataProps]);

  // Bind functions to Web Component as Events
  React.useEffect(() => {
    const component = componentRef.current;
    if (!component) return;
    functionKeys.forEach((fKey) =>
      component.addEventListener(fKey, props[fKey]),
    );
    return (): void =>
      functionKeys.forEach((fKey) =>
        component.removeEventListener(fKey, props[fKey]),
      );
  }, [componentRef, props, functionKeys]);
}

export interface TeEventListeners {
  styles?: import('lit').CSSResult[];
  /**
   * For robot tests to identify the interactable elements
   */
  testId?: string;
  forceUpdate?: () => void;
  sendEvent?: (name: string, e: Event | CustomEvent) => void;
  sendCustomEvent?: (name: string, value: unknown) => void;
  forwardCustomEvent?: (e: CustomEvent) => void;
  teChange?: (e: Event | CustomEvent) => void;
  teClick?: (e: Event | CustomEvent) => void;
  teToggle?: (e: Event | CustomEvent) => void;
  teFocus?: (e: Event | CustomEvent) => void;
  teBlur?: (e: Event | CustomEvent) => void;
  teClose?: (e: Event | CustomEvent) => void;
  teMouseEnter?: (e: Event | CustomEvent) => void;
  teMouseExit?: (e: Event | CustomEvent) => void;
  teKeyDown?: (e: Event | CustomEvent) => void;
  teDropdownClear?: (e: Event | CustomEvent) => void;
}

export declare const pathByName: {
  'drag-right': string;
  'angled-menu': string;
  'chevron-left': string;
  'chevron-right': string;
  'chevron-up': string;
  'chevron-down': string;
  'arrow-right': string;
  'arrow-left': string;
  'arrow-up-circle': string;
  'triangle-left': string;
  'triangle-right': string;
  'triangle-up': string;
  'triangle-down': string;
  'map-pin': string;
  'user-add': string;
  'upload-thin': string;
  'upload-error': string;
  'more-items': string;
  'more-items-vertical': string;
  'schedule-add': string;
  'schedule-list': string;
  'schedule-stack': string;
  'site-contacts': string;
  'eye-show': string;
  'eye-hide': string;
  'schedule-date': string;
  'sites-landscape': string;
  'sites-grid': string;
  'in-inspection': string;
  'has-issues': string;
  'drag-handle': string;
  'copy-code': string;
  'indeterminate-checkmark': string;
  location: string;
  users: string;
  configure: string;
  complete: string;
  info: string;
  verified: string;
  bell: string;
  checkmark: string;
  checkmarkcircle: string;
  pageNotFound: string;
  resources: string;
  close: string;
  delete: string;
  edit: string;
  editPolygon: string;
  expand: string;
  collapse: string;
  filter: string;
  grid: string;
  layers: string;
  map: string;
  menu: string;
  milestone: string;
  minus: string;
  pdf: string;
  pentagon: string;
  plus: string;
  point: string;
  polygon: string;
  addSubAreaPolygon: string;
  editSubAreaPolygon: string;
  schedule: string;
  search: string;
  share: string;
  tiff: string;
  upload: string;
  user: string;
  cloudUp: string;
  resize: string;
  link: string;
  contacts: string;
  image: string;
  snapshot: string;
  newDocument: string;
  trashbin: string;
  download: string;
  print: string;
  block: string;
  undo: string;
  listView: string;
  previous: string;
  next: string;
  cloud: string;
  columns: string;
  gallery: string;
  list: string;
  warning: string;
};
